import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The design-system page: its labels and the sample content it shows the components with.
// Component and product names ("Instagram", "Google Maps", "Starter") read the same in both languages.

export type StyleguideCopyKey = keyof typeof EN;
export type StyleguideCatalog = typeof EN;

const EN = {
  screenTitle: "Design system · {product}",
  navLabel: "Design system",

  // Page header
  title: "Design system",
  description:
    "Every component the platform is built from, in its real states. Screens compose these; they never style raw elements.",
  breadcrumbDashboard: "Dashboard",
  showToast: "Show toast",
  openDrawer: "Open drawer",
  openDialog: "Open dialog",
  toastSavedTitle: "Saved",
  toastSavedDescription: "Toasts announce outcomes.",

  // Toast gallery (D997-D1001): one trigger per state the screenshot baseline captures.
  galleryToastTitle: "Toast states",
  galleryProseTitle: "Prose",
  galleryProseDescription: "A blog post's body, element by element, as MDX renders it.",
  galleryProseHeading: "A heading in a post",
  galleryProseParagraph: "A paragraph of a post, with a link and some code in it:",
  galleryProseLink: "a link",
  galleryProseMark: "a marked phrase",
  galleryProseImage: "An image in a post",
  galleryBookingTitle: "Booking",
  galleryBookingDescription:
    "The words beside a calendar. The calendar itself loads only on a site whose policy lets Cal.com in.",
  galleryBookingWords: "The words sit here.",
  galleryBookingCalendar: "The calendar sits here.",
  galleryBrandFileTitle: "Brand file",
  galleryBrandFileDescription: "Another brand's own logo file, kept to its frame.",
  galleryProseItem: "An item",
  galleryProseCode: "pnpm check",
  galleryProseColumn: "A column",
  galleryToastDescription: "Every shape toast() takes; the baseline screenshots are taken from these.",
  galleryToastPlain: "Plain",
  galleryToastWithDescription: "With description",
  galleryToastWithAction: "With action",
  galleryToastSticky: "Sticky",
  galleryToastEveryTone: "Every tone",
  galleryPlainTitle: "Saved",
  galleryDescriptionTitle: "Export ready",
  galleryDescriptionBody: "Your export is ready to download.",
  galleryActionTitle: "Mailbox disconnected",
  galleryActionDescription: "Reconnect it to keep reading replies.",
  galleryActionLabel: "Reconnect",
  galleryStickyTitle: "A new version is available",
  galleryStickyDescription: "Reload to get the latest version.",
  galleryStickyAction: "Reload",
  toneNeutral: "Neutral",
  tonePrimary: "Primary",
  toneSuccess: "Success",
  toneWarning: "Warning",
  toneDanger: "Danger",
  toneInfo: "Info",

  // Banners
  bannerNoSubscriptionTitle: "No Active Subscription",
  bannerNoSubscriptionDescription: "You don't have any active subscription.",
  bannerActivate: "Activate",
  bannerTrialTitle: "You've used up your trial credits",
  bannerTrialDescription: "The {count} contacts you already extracted are yours and they're safe.",
  bannerSeePlans: "See plans",
  bannerPublicTitle: "Only public accounts",
  bannerPublicDescription: "The search bar only lists public accounts.",
  bannerPaymentTitle: "Payment failed",
  bannerPaymentDescription:
    "We couldn't charge your card ending in 4242. Update it to keep your campaigns sending.",
  bannerUpdateCard: "Update card",
  bannerGlowErrorTitle: "Campaign paused: bounce rate over 8%",
  bannerGlowErrorDescription:
    "The glow version, on the alarm Warp. For the one failure the reader must handle now.",
  bannerReviewCampaign: "Review campaign",
  bannerGlowWarningDescription: "The glow version: one per screen, for the notice that matters most.",

  // Effects
  effectsTitle: "Effects",
  effectsDescription: "Opt-in, one per moment. Nothing glows or shimmers by default.",
  askProduct: "Ask {product}",
  toastStartedTitle: "Started",
  toastStartedDescription: "The AI action ran.",
  showSplash: "Show loading splash",
  shimmerText: "Finding your first leads…",
  workingFieldLabel: "Working field",
  workingFieldBusy: "Writing your first email…",
  workingFieldDone: "Done. Review it below.",
  workingSwitch: "Working",
  splashLabel: "Loading",

  // Command palette
  paletteTitle: "Command palette",
  paletteDescription: "⌘K anywhere in the app, or the launcher on a home page.",
  palettePlaceholder: "Search pages or start something",

  // Buttons
  buttonsTitle: "Buttons",
  buttonsDescription: "Variants and sizes.",
  buttonPrimary: "Primary",
  buttonSecondary: "Secondary",
  buttonSoft: "Soft",
  buttonGhost: "Ghost",
  buttonDanger: "Danger",
  buttonLink: "Link",
  buttonSmall: "Small",
  buttonMedium: "Medium",
  buttonLarge: "Large",
  buttonDisabled: "Disabled",
  menuLabel: "Analysis actions",
  menuRename: "Rename",
  menuViewExports: "View exports",
  menuAddToQueue: "Add to queue",
  menuDelete: "Delete",
  toastQueue: "Queue",
  toastExports: "Exports",
  toastDeleted: "Deleted",

  // Status
  statusTitle: "Status",
  statusDescription: "Tones always carry a label.",
  statusRunning: "Running",
  statusCompleted: "Completed",
  statusNotStarted: "Not started",
  statusPaused: "Paused",
  statusError: "Error",
  pillDraft: "Draft",
  pillAi: "AI",
  pillHealthy: "Healthy",
  pillSomeBounces: "Some bounces",
  pillHighBounces: "High bounces",
  pillWarming: "Warming",
  badgePremium: "Premium",
  progressCompletion: "Completion",
  distributionLabel: "Analyses by status",

  // Inputs
  inputsTitle: "Inputs",
  fieldAnalysisName: "Analysis name",
  fieldAnalysisHint: "Only you see this name.",
  fieldAnalysisPlaceholder: "Followers of @account",
  fieldInstagramUsername: "Instagram username",
  fieldInstagramError: "This account is private.",
  fieldDescribe: "Describe who you want to find",
  fieldDescribePlaceholder: "Coffee roasters in Spain with more than 5k followers…",
  selectPlatform: "Platform",
  platformInstagram: "Instagram",
  platformTwitter: "X (Twitter)",
  platformGoogleMaps: "Google Maps",
  searchPlaceholder: "Search by name, username or email",
  checkboxLabel: "Skip already-contacted prospects",
  checkboxDescription: "Never email the same person from two campaigns.",
  switchAdvancedLabel: "Advanced view",
  switchAdvancedDescription: "Show every counter of the run.",
  segmentedLabel: "Billing period",
  segmentedMonthly: "Monthly",
  segmentedYearly: "Yearly",
  postRowDate: "October 5, 2026",
  postRowTitle: "A post's title",
  postRowDescription: "A post's description, on a row with a thin line under it.",

  // Data display
  dataTitle: "Data display",
  usageCredits: "Credits",
  progressLeft: "{left} left",
  itemListTitle: "Latest analyses",
  itemCreated: "Created 2 days ago",
  itemLeads: "1,240 leads",
  itemCompleted: "Completed",
  usageAiCredits: "AI credits",
  usageEmailAccounts: "Email accounts",
  factPlan: "Plan",
  factPlanValue: "Starter",
  factRenews: "Renews",
  factRenewsValue: "1 Nov 2026",
  factProjects: "Projects",
  factProjectsValue: "{used} of {total}",
  tooltipCredits: "Credits are spent per profile examined, whether or not an email is found.",
  hoverMe: "Hover me",

  // Status samples
  tileTotalUsers: "Total users",
  tileProfilesAnalyzed: "Profiles analyzed",
  tileUsedCredits: "Used credits",
  tileEmails: "Emails",
  cardOrganizations: "Organizations",
  cardOrganizationsHint: "{count} paying",
  cardQueueLag: "Queue lag",
  cardQueueStatus: "Draining",
  cardWorker: "Worker",
  cardWorkerValue: "7 min ago",
  cardWorkerStatus: "No heartbeat for 7 min",
  sampleIdentityName: "Ada Lovelace",

  // Wizard
  wizardTitle: "Wizard",
  wizardLabel: "New analysis",
  stepPlatform: "Platform",
  stepPlatformDescription: "Data source",
  stepTarget: "Target",
  stepTargetDescription: "What to extract",
  stepConfiguration: "Configuration",
  stepConfigurationDescription: "Target and settings",

  // Tabs and table
  tabsLabel: "Campaign",
  tabTable: "Table",
  tabEmpty: "Empty",
  tabLocked: "Premium lock",
  tableLabel: "Analyses",
  tableSearchPlaceholder: "Search analyses",
  columnName: "Analysis name",
  columnLeads: "Leads analysed",
  columnStatus: "Status",
  columnCompletion: "Completion",
  columnActions: "Actions",
  rowActionsLabel: "Row actions",
  rowActionOpen: "Open",
  rowOneName: "Followers of @fruitlogistica",
  rowOneHandle: "Created a year ago",
  rowTwoName: "Paid Analysis",
  rowTwoHandle: "Created 6 months ago",
  rowThreeName: "Coffee shops · Valencia",
  rowThreeHandle: "Created 3 days ago",

  // Empty state and premium lock
  emptyTitle: "No inbox accounts configured",
  emptyDescription: "Enable inbox functionality for your email accounts to start managing conversations.",
  emptyAction: "Manage email accounts",
  lockedTitle: "Filters are not available on your plan",
  lockedDescription: "Build reusable audiences from several analyses with Starter or above.",
  lockedPanelTitle: "Fitness influencers +10K followers",
  lockedPanelDescription: "3 analyses · 1,240 prospects",
  lockedPreview: "Preview of a gated screen.",

  // Drawer and dialog
  drawerTitle: "Rename analysis",
  drawerDescription: "Forms, records and pickers open in a drawer; only questions open a dialog.",
  drawerCancel: "Cancel",
  drawerSave: "Save",
  drawerFieldName: "Name",
  drawerFieldValue: "Fitness influencers +10K followers",
  disclosureTitle: "Settings",
  disclosureSummary: "Optional parts stay folded until opened",
  autoEnrich: "Auto Enrich",
  dialogTitle: "Another analysis is already running",
  dialogDescription: "Stop it and start this one? Nothing it has already found is lost.",
  dialogCancel: "Keep running",
  dialogConfirm: "Stop and start",
  dialogBody: "Your plan runs one analysis at a time. Parallel Analysis lifts the limit.",
} as const;

const ES: Translation<StyleguideCatalog> = {
  screenTitle: "Sistema de diseño · {product}",
  navLabel: "Sistema de diseño",

  title: "Sistema de diseño",
  description:
    "Todos los componentes con los que se construye la plataforma, en sus estados reales. Las pantallas los componen; nunca dan estilo a elementos sueltos.",
  breadcrumbDashboard: "Panel",
  showToast: "Mostrar aviso",
  openDrawer: "Abrir panel lateral",
  openDialog: "Abrir diálogo",
  toastSavedTitle: "Guardado",
  toastSavedDescription: "Los avisos anuncian resultados.",

  galleryToastTitle: "Estados del aviso",
  galleryProseTitle: "Prosa",
  galleryProseDescription: "El cuerpo de un artículo del blog, elemento a elemento, como lo muestra MDX.",
  galleryProseHeading: "Un encabezado en un artículo",
  galleryProseParagraph: "Un párrafo de un artículo, con un enlace y algo de código:",
  galleryProseLink: "un enlace",
  galleryProseMark: "una frase destacada",
  galleryProseImage: "Una imagen en un artículo",
  galleryBookingTitle: "Reserva",
  galleryBookingDescription:
    "Las palabras junto a un calendario. El calendario solo carga en un sitio cuya política deja entrar a Cal.com.",
  galleryBookingWords: "Aquí van las palabras.",
  galleryBookingCalendar: "Aquí va el calendario.",
  galleryBrandFileTitle: "Archivo de marca",
  galleryBrandFileDescription: "El logotipo de otra marca, ajustado a su marco.",
  galleryProseItem: "Un elemento",
  galleryProseCode: "pnpm check",
  galleryProseColumn: "Una columna",
  galleryToastDescription: "Cada forma que toma toast(); de aquí salen las capturas de referencia.",
  galleryToastPlain: "Sencillo",
  galleryToastWithDescription: "Con descripción",
  galleryToastWithAction: "Con acción",
  galleryToastSticky: "Persistente",
  galleryToastEveryTone: "Cada tono",
  galleryPlainTitle: "Guardado",
  galleryDescriptionTitle: "Exportación lista",
  galleryDescriptionBody: "Tu exportación está lista para descargar.",
  galleryActionTitle: "Buzón desconectado",
  galleryActionDescription: "Vuelve a conectarlo para seguir leyendo las respuestas.",
  galleryActionLabel: "Reconectar",
  galleryStickyTitle: "Hay una nueva versión disponible",
  galleryStickyDescription: "Recarga para obtener la última versión.",
  galleryStickyAction: "Recargar",
  toneNeutral: "Neutral",
  tonePrimary: "Primario",
  toneSuccess: "Éxito",
  toneWarning: "Advertencia",
  toneDanger: "Peligro",
  toneInfo: "Información",

  bannerNoSubscriptionTitle: "Sin suscripción activa",
  bannerNoSubscriptionDescription: "No tienes ninguna suscripción activa.",
  bannerActivate: "Activar",
  bannerTrialTitle: "Has usado todos tus créditos de prueba",
  bannerTrialDescription: "Los {count} contactos que ya extrajiste son tuyos y están a salvo.",
  bannerSeePlans: "Ver planes",
  bannerPublicTitle: "Solo cuentas públicas",
  bannerPublicDescription: "La barra de búsqueda solo muestra cuentas públicas.",
  bannerPaymentTitle: "El pago falló",
  bannerPaymentDescription:
    "No pudimos cobrar tu tarjeta terminada en 4242. Actualízala para que tus campañas sigan enviándose.",
  bannerUpdateCard: "Actualizar tarjeta",
  bannerGlowErrorTitle: "Campaña en pausa: tasa de rebote superior al 8%",
  bannerGlowErrorDescription:
    "La versión con brillo, sobre el Warp de alarma. Para el único fallo que quien lee debe atender ahora.",
  bannerReviewCampaign: "Revisar campaña",
  bannerGlowWarningDescription: "La versión con brillo: una por pantalla, para el aviso que más importa.",

  effectsTitle: "Efectos",
  effectsDescription: "Opcionales, uno por momento. Nada brilla ni reluce por defecto.",
  askProduct: "Pregunta a {product}",
  toastStartedTitle: "Iniciado",
  toastStartedDescription: "Se ejecutó la acción de IA.",
  showSplash: "Mostrar pantalla de carga",
  shimmerText: "Buscando tus primeros contactos…",
  workingFieldLabel: "Campo en trabajo",
  workingFieldBusy: "Escribiendo tu primer email…",
  workingFieldDone: "Listo. Revísalo abajo.",
  workingSwitch: "Trabajando",
  splashLabel: "Cargando",

  paletteTitle: "Paleta de comandos",
  paletteDescription: "⌘K en cualquier parte de la app, o el buscador en una página de inicio.",
  palettePlaceholder: "Busca páginas o empieza algo",

  buttonsTitle: "Botones",
  buttonsDescription: "Variantes y tamaños.",
  buttonPrimary: "Principal",
  buttonSecondary: "Secundario",
  buttonSoft: "Suave",
  buttonGhost: "Fantasma",
  buttonDanger: "Peligro",
  buttonLink: "Enlace",
  buttonSmall: "Pequeño",
  buttonMedium: "Mediano",
  buttonLarge: "Grande",
  buttonDisabled: "Desactivado",
  menuLabel: "Acciones del análisis",
  menuRename: "Renombrar",
  menuViewExports: "Ver exportaciones",
  menuAddToQueue: "Añadir a la cola",
  menuDelete: "Eliminar",
  toastQueue: "Cola",
  toastExports: "Exportaciones",
  toastDeleted: "Eliminado",

  statusTitle: "Estado",
  statusDescription: "Los tonos siempre llevan una etiqueta.",
  statusRunning: "En curso",
  statusCompleted: "Completado",
  statusNotStarted: "Sin iniciar",
  statusPaused: "En pausa",
  statusError: "Error",
  pillDraft: "Borrador",
  pillAi: "IA",
  pillHealthy: "Saludable",
  pillSomeBounces: "Algunos rebotes",
  pillHighBounces: "Muchos rebotes",
  pillWarming: "Calentando",
  badgePremium: "Premium",
  progressCompletion: "Avance",
  distributionLabel: "Análisis por estado",

  inputsTitle: "Campos",
  fieldAnalysisName: "Nombre del análisis",
  fieldAnalysisHint: "Solo tú ves este nombre.",
  fieldAnalysisPlaceholder: "Seguidores de @cuenta",
  fieldInstagramUsername: "Usuario de Instagram",
  fieldInstagramError: "Esta cuenta es privada.",
  fieldDescribe: "Describe a quién quieres encontrar",
  fieldDescribePlaceholder: "Tostadores de café en España con más de 5k seguidores…",
  selectPlatform: "Plataforma",
  platformInstagram: "Instagram",
  platformTwitter: "X (Twitter)",
  platformGoogleMaps: "Google Maps",
  searchPlaceholder: "Busca por nombre, usuario o email",
  checkboxLabel: "Omitir prospectos ya contactados",
  checkboxDescription: "Nunca escribas a la misma persona desde dos campañas.",
  switchAdvancedLabel: "Vista avanzada",
  switchAdvancedDescription: "Muestra todos los contadores de la ejecución.",
  segmentedLabel: "Periodo de facturación",
  segmentedMonthly: "Mensual",
  segmentedYearly: "Anual",
  postRowDate: "5 de octubre de 2026",
  postRowTitle: "El título de un artículo",
  postRowDescription: "La descripción de un artículo, en una fila con una línea fina debajo.",

  dataTitle: "Datos en pantalla",
  usageCredits: "Créditos",
  progressLeft: "Quedan {left}",
  itemListTitle: "Últimos análisis",
  itemCreated: "Creado hace 2 días",
  itemLeads: "1.240 leads",
  itemCompleted: "Completado",
  usageAiCredits: "Créditos de IA",
  usageEmailAccounts: "Cuentas de email",
  factPlan: "Plan",
  factPlanValue: "Starter",
  factRenews: "Se renueva",
  factRenewsValue: "1 nov 2026",
  factProjects: "Proyectos",
  factProjectsValue: "{used} de {total}",
  tooltipCredits: "Los créditos se gastan por perfil examinado, se encuentre o no un email.",
  hoverMe: "Pasa el cursor",

  tileTotalUsers: "Usuarios totales",
  tileProfilesAnalyzed: "Perfiles analizados",
  tileUsedCredits: "Créditos usados",
  tileEmails: "Emails",
  cardOrganizations: "Organizaciones",
  cardOrganizationsHint: "{count} de pago",
  cardQueueLag: "Retraso de la cola",
  cardQueueStatus: "Vaciándose",
  cardWorker: "Worker",
  cardWorkerValue: "hace 7 min",
  cardWorkerStatus: "Sin señal desde hace 7 min",
  sampleIdentityName: "Ada Lovelace",

  wizardTitle: "Asistente",
  wizardLabel: "Nuevo análisis",
  stepPlatform: "Plataforma",
  stepPlatformDescription: "Fuente de datos",
  stepTarget: "Objetivo",
  stepTargetDescription: "Qué extraer",
  stepConfiguration: "Configuración",
  stepConfigurationDescription: "Objetivo y ajustes",

  tabsLabel: "Campaña",
  tabTable: "Tabla",
  tabEmpty: "Vacío",
  tabLocked: "Bloqueo premium",
  tableLabel: "Análisis",
  tableSearchPlaceholder: "Buscar análisis",
  columnName: "Nombre del análisis",
  columnLeads: "Contactos analizados",
  columnStatus: "Estado",
  columnCompletion: "Avance",
  columnActions: "Acciones",
  rowActionsLabel: "Acciones de la fila",
  rowActionOpen: "Abrir",
  rowOneName: "Seguidores de @fruitlogistica",
  rowOneHandle: "Creado hace un año",
  rowTwoName: "Análisis de pago",
  rowTwoHandle: "Creado hace 6 meses",
  rowThreeName: "Cafeterías · Valencia",
  rowThreeHandle: "Creado hace 3 días",

  emptyTitle: "No hay cuentas de bandeja configuradas",
  emptyDescription:
    "Activa la bandeja de entrada en tus cuentas de email para empezar a gestionar conversaciones.",
  emptyAction: "Gestionar cuentas de email",
  lockedTitle: "Los filtros no están disponibles en tu plan",
  lockedDescription: "Crea audiencias reutilizables a partir de varios análisis con Starter o superior.",
  lockedPanelTitle: "Influencers de fitness con +10K seguidores",
  lockedPanelDescription: "3 análisis · 1.240 prospectos",
  lockedPreview: "Vista previa de una pantalla bloqueada.",

  drawerTitle: "Renombrar análisis",
  drawerDescription:
    "Los formularios, registros y selectores se abren en un panel lateral; solo las preguntas abren un diálogo.",
  drawerCancel: "Cancelar",
  drawerSave: "Guardar",
  drawerFieldName: "Nombre",
  drawerFieldValue: "Influencers de fitness con +10K seguidores",
  disclosureTitle: "Ajustes",
  disclosureSummary: "Las partes opcionales siguen plegadas hasta que las abres",
  autoEnrich: "Enriquecer automáticamente",
  dialogTitle: "Ya hay otro análisis en curso",
  dialogDescription: "¿Detenerlo y empezar este? Nada de lo que ya encontró se pierde.",
  dialogCancel: "Seguir en curso",
  dialogConfirm: "Detener y empezar",
  dialogBody: "Tu plan ejecuta un análisis a la vez. Parallel Analysis quita el límite.",
};

export const STYLEGUIDE_CATALOGS: Catalogs<StyleguideCatalog> = { en: EN, es: ES };
